
// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { meetingAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Video,
  Calendar,
  Clock,
  ExternalLink,
  Loader2,
  Users,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/utils";
import PresenceTracker from "@/components/presence/PresenceTracker";
import ScreenShareProvider from "@/components/surveillance/ScreenShareProvider";

export default function MeetingRoomPage() {
  const { workspaceId, meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMeeting, setShowMeeting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user from localStorage on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }
  }, []);

  const isModeratorRole = currentUser?.role === "admin" || currentUser?.role === "trainer";

  useEffect(() => {
    loadMeeting();
  }, [meetingId]);

  const loadMeeting = async () => {
    try {
      const response = await meetingAPI.get(workspaceId, meetingId);
      setMeeting(response.data);
    } catch (error) {
      console.error("Failed to load meeting:", error);
      toast.error("Erreur lors du chargement de la réunion");
      navigate(`/workspace/${workspaceId}/meetings`);
    } finally {
      setLoading(false);
    }
  };

  const getMeetingTypeLabel = (type) => {
    const types = {
      jitsi: "Jitsi",
      zoom: "Zoom",
      google_meet: "Google Meet",
      teams: "Microsoft Teams",
    };
    return types[type] || type;
  };

  const openMeetingExternal = () => {
    if (meeting?.meeting_url) {
      window.open(meeting.meeting_url, '_blank');
    }
  };

  const joinMeeting = () => {
    // For external services (Zoom, Google Meet, Teams), open in new tab
    // as they don't allow iframe embedding for security reasons
    if (meeting?.meeting_type !== "jitsi") {
      window.open(meeting.meeting_url, '_blank');
      setShowMeeting(true); // Activate presence tracking even for external meetings
      toast.success("Réunion ouverte dans un nouvel onglet");
      return;
    }
    
    // For Jitsi, use embedded iframe
    setShowMeeting(true);
    toast.success("Connexion à la réunion...");
  };

  const leaveMeeting = () => {
    setShowMeeting(false);
    toast.success("Réunion terminée");
    // Redirect back to meetings list
    setTimeout(() => {
      navigate(`/workspace/${workspaceId}/meetings`);
    }, 500);
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/meetings`)}
            disabled={showMeeting}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">{meeting?.title}</h1>
              <Badge variant="secondary">{getMeetingTypeLabel(meeting?.meeting_type)}</Badge>
              {isModeratorRole && (
                <Badge variant="outline" className="gap-1">
                  <Shield className="h-3 w-3" />
                  Modérateur
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDateTime(meeting?.scheduled_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {meeting?.duration_minutes} minutes
              </span>
            </div>
          </div>
          
          {!showMeeting ? (
            <div className="flex gap-2">
              <Button onClick={openMeetingExternal} variant="outline" className="gap-2">
                <ExternalLink className="h-4 w-4" />
                Nouvel onglet
              </Button>
              <Button onClick={joinMeeting} className="gap-2" size="lg">
                <Video className="h-5 w-5" />
                Rejoindre la réunion
              </Button>
            </div>
          ) : (
            <Button onClick={leaveMeeting} variant="destructive" className="gap-2">
              <Video className="h-5 w-5" />
              Quitter la réunion
            </Button>
          )}
        </div>

        {meeting?.description && !showMeeting && (
          <p className="text-muted-foreground mb-6">{meeting.description}</p>
        )}

        {/* Meeting Room */}
        {meeting?.meeting_url && (
          <div className="space-y-4">
            {/* Meeting Link Info - only show when not in meeting */}
            {!showMeeting && (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Lien de la réunion</p>
                  <p className="text-sm text-muted-foreground break-all">{meeting.meeting_url}</p>
                </div>
                <Button variant="outline" onClick={openMeetingExternal} className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir
                </Button>
              </div>
            )}

            {/* Lobby Info */}
            {!showMeeting && meeting.meeting_type === "jitsi" && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="font-medium text-blue-800 dark:text-blue-200">Salle d'attente activée</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {isModeratorRole 
                      ? "En tant que formateur/admin, vous pouvez approuver les participants dans la salle d'attente."
                      : "Vous devrez attendre l'approbation du formateur pour rejoindre la réunion."
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Info for external services */}
            {!showMeeting && meeting.meeting_type !== "jitsi" && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <ExternalLink className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Réunion {getMeetingTypeLabel(meeting.meeting_type)}
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Cette réunion s'ouvrira dans un nouvel onglet. {getMeetingTypeLabel(meeting.meeting_type)} ne permet pas l'intégration directe pour des raisons de sécurité.
                  </p>
                </div>
              </div>
            )}

            {/* Meeting iframe */}
            {showMeeting && (
              <div className="border rounded-lg overflow-hidden bg-black" style={{ height: "70vh" }}>
                <iframe
                  src={meeting.meeting_url}
                  className="w-full h-full"
                  allow="camera; microphone; fullscreen; display-capture; autoplay"
                  allowFullScreen
                  title={meeting.title}
                />
              </div>
            )}

            {/* Presence Tracker — for all meeting types */}
            {showMeeting && (
              <PresenceTracker sessionId={`meeting:${meetingId}`} workspaceId={workspaceId} />
            )}

            {/* Screen Share Provider — for candidates (non-staff) in active meetings */}
            {showMeeting && !isModeratorRole && (
              <div className="mt-4">
                <ScreenShareProvider
                  meetingId={meetingId}
                  userId={currentUser?.id}
                  userName={`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`}
                />
              </div>
            )}

            {/* Placeholder when not in meeting */}
            {!showMeeting && (
              <div className="flex flex-col items-center justify-center py-16 border rounded-lg bg-gradient-to-b from-muted/50 to-muted">
                <Video className="h-16 w-16 text-primary mb-4" />
                <h3 className="text-lg font-semibold">Prêt à rejoindre ?</h3>
                <p className="text-muted-foreground mt-2 text-center max-w-md">
                  Cliquez sur "Rejoindre la réunion" pour entrer dans la salle.
                  {!isModeratorRole && " Vous serez placé en salle d'attente jusqu'à l'approbation du formateur."}
                </p>
                <Button onClick={joinMeeting} className="gap-2 mt-6" size="lg">
                  <Video className="h-5 w-5" />
                  Rejoindre la réunion
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
